import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, X, PoundSterling, Calculator,
  ShieldCheck, Sparkles, Server, Globe2
} from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';

/**
 * /microsoft-calling-plans-vs-teammate — bottom-of-funnel comparison page.
 *
 * Aimed at Teams admins / IT decision-makers who are evaluating
 * Microsoft Calling Plans against a Direct Routing carrier (VoIPinger
 * TeamMate). Built around a TCO calculator and a side-by-side feature
 * matrix so the decision is obvious without booking a sales call.
 *
 * Same DNA as /microsoft-teams's comparison table, but with its own URL
 * for SEO ("Microsoft Calling Plans vs Direct Routing", "Teams Phone
 * cost comparison", "Calling Plan alternative UK") and an inline
 * calculator that gives a personalised number.
 */
const FAQ = [
  { q: 'Will my users keep using Microsoft Teams?', a: 'Yes — exactly the same Teams client. TeamMate plugs into the Microsoft Teams tenant via Direct Routing, so the call experience is unchanged. The only difference is the carrier behind every call.' },
  { q: 'Do we keep our Microsoft licences?', a: 'You keep your existing Microsoft 365 / E3 / E5 estate. The only change is replacing the Microsoft Calling Plan licence with a Microsoft Teams Phone licence (or E5 if you already have it) — plus a TeamMate seat on our side.' },
  { q: "Is the Direct Routing SBC included?", a: 'Yes. TeamMate ships with a Microsoft-certified Ribbon SBC at no extra cost. No co-location, no separate hardware budget, no manual co-management.' },
  { q: "What's the realistic saving on a 50-user UK tenant?", a: 'Most UK SMBs save £4,500–£8,000 over 12 months on a 50-user tenant. International-heavy callers see closer to 60% lower per-minute costs. Use the calculator above for a personalised figure.' },
  { q: 'How long does the switch take?', a: 'Most tenants are live in 48 hours once licences are in place. Number porting is the slowest step at 5–10 working days. We cut over out-of-hours with a rollback plan.' },
  { q: 'Will my numbers move with us?', a: 'Yes. We port your existing UK or international DDIs (single or range), including non-geographic. Porting is coordinated by us; there is no downtime during the cutover.' },
  { q: 'Can we record calls for compliance?', a: 'Yes. Compliance-grade call recording is bundled with TeamMate at no extra cost, including PCI-DSS pause/resume and configurable retention. Microsoft charges for compliance recording as an add-on.' }
];

const COMPARISON_ROWS = [
  // groups: pricing, voice, numbers, features, compliance, support
  { group: 'Pricing', label: 'Per-user monthly cost', mcp: '£10–£18', vp: 'From £8' },
  { group: 'Pricing', label: 'International call rates', mcp: 'Premium', vp: 'Wholesale' },
  { group: 'Pricing', label: 'Setup fees', mcp: 'Per-user', vp: 'Zero setup' },

  { group: 'Voice', label: 'HD voice (G.722 / Opus)', mcp: true, vp: true },
  { group: 'Voice', label: 'Microsoft-certified SBC included', mcp: false, vp: true },
  { group: 'Voice', label: 'Number porting included', mcp: 'Limited', vp: true },
  { group: 'Voice', label: 'Non-geographic & international DDIs', mcp: false, vp: true },
  { group: 'Voice', label: 'UK 0800 / 03xx numbers', mcp: 'Limited', vp: true },

  { group: 'Compliance', label: 'Compliance call recording', mcp: 'Add-on', vp: true },
  { group: 'Compliance', label: 'PCI-DSS pause / resume', mcp: false, vp: true },
  { group: 'Compliance', label: 'GDPR data residency UK', mcp: 'Limited', vp: true },
  { group: 'Compliance', label: 'Audit log retention', mcp: '90 days', vp: 'Configurable' },

  { group: 'Contact Centre', label: 'Multi-level IVR builder', mcp: false, vp: true },
  { group: 'Contact Centre', label: 'Skill-based routing', mcp: false, vp: true },
  { group: 'Contact Centre', label: 'Live wallboards', mcp: false, vp: true },
  { group: 'Contact Centre', label: 'Sentiment Analysis on recordings', mcp: false, vp: true },
  { group: 'Contact Centre', label: 'Conversational Transcription', mcp: 'Premium add-on', vp: true },

  { group: 'Support', label: 'UK-based support out of London DC', mcp: false, vp: true },
  { group: 'Support', label: '24/7 NOC', mcp: false, vp: true },
  { group: 'Support', label: '99.99% uptime SLA', mcp: 'Tier varies', vp: true }
];

export default function CallingPlansVsTeamMate() {
  // TCO calculator state
  const [users, setUsers] = useState(50);
  const [mcpPrice, setMcpPrice] = useState(14);  // typical mid-tier MCP £/user/mo
  const [vpPrice, setVpPrice] = useState(8);     // TeamMate seat £/user/mo

  const tco = useMemo(() => {
    const mcpYear = users * mcpPrice * 12;
    const vpYear = users * vpPrice * 12;
    const savings = mcpYear - vpYear;
    const pct = mcpYear ? Math.round((savings / mcpYear) * 100) : 0;
    return { mcpYear, vpYear, savings, pct };
  }, [users, mcpPrice, vpPrice]);

  const fmt = (n) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

  // Group the comparison rows for the table layout
  const groups = useMemo(() => {
    const out = {};
    for (const r of COMPARISON_ROWS) (out[r.group] ||= []).push(r);
    return out;
  }, []);

  return (
    <>
      <Seo
        title="Microsoft Calling Plans vs Direct Routing TCO Calculator | VoIPinger"
        description="The UK Direct Routing alternative to Microsoft Calling Plans. Free TCO calculator, side-by-side feature matrix, and a 12-month savings estimate in 30 seconds."
        path="/microsoft-calling-plans-vs-teammate"
        image="https://voipinger.com/assets/heroes/calling-plans-vs-teammate.png"
        product={{ name: 'TeamMate vs Microsoft Calling Plans', description: 'Direct Routing alternative to Microsoft Calling Plans with TCO calculator and feature matrix.' }}
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Microsoft Teams', path: '/microsoft-teams' }, { name: 'Calling Plans vs TeamMate', path: '/microsoft-calling-plans-vs-teammate' }]}
      />
      <PageHero
        eyebrow="Microsoft Calling Plans vs TeamMate"
        title="The UK Direct Routing alternative to Microsoft Calling Plans"
        subtitle="Same Microsoft Teams interface. Half the cost. Twice the features. See the full side-by-side, then run your own TCO in under 30 seconds."
        breadcrumb="Calling Plans vs TeamMate"
      />

      {/* ============================================================== */}
      {/* TCO CALCULATOR                                                   */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-6 md:p-10 shadow-xl shadow-violet-500/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Calculator size={22} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest font-bold text-violet-600">12-month TCO calculator</p>
                <h2 className="text-2xl font-extrabold text-slate-900">What would the switch save you?</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Number of users</label>
                <input
                  type="range" min="5" max="500" step="5" value={users}
                  onChange={(e) => setUsers(Number(e.target.value))}
                  className="w-full accent-violet-600"
                />
                <input
                  type="number" min="1" max="5000" value={users}
                  onChange={(e) => setUsers(Math.max(1, Number(e.target.value) || 1))}
                  className="w-full mt-2 px-3 py-2 rounded-lg border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-100 outline-none font-bold text-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Microsoft Calling Plan £/user/mo</label>
                <input
                  type="range" min="6" max="20" step="1" value={mcpPrice}
                  onChange={(e) => setMcpPrice(Number(e.target.value))}
                  className="w-full accent-rose-500"
                />
                <div className="mt-2 px-3 py-2 rounded-lg bg-white border border-slate-300 font-bold text-lg flex items-center gap-1">
                  <PoundSterling size={16} className="text-slate-500" /> {mcpPrice}.00
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-violet-700 mb-2">TeamMate £/user/mo</label>
                <input
                  type="range" min="6" max="20" step="1" value={vpPrice}
                  onChange={(e) => setVpPrice(Number(e.target.value))}
                  className="w-full accent-violet-600"
                />
                <div className="mt-2 px-3 py-2 rounded-lg bg-white border border-violet-300 font-bold text-lg flex items-center gap-1 text-violet-700">
                  <PoundSterling size={16} /> {vpPrice}.00
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="rounded-2xl bg-white border border-slate-200 p-4 text-center">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">12-mo Calling Plans</p>
                <p className="text-xl md:text-2xl font-extrabold text-rose-600 mt-1">{fmt(tco.mcpYear)}</p>
              </div>
              <div className="rounded-2xl bg-white border border-violet-200 p-4 text-center">
                <p className="text-[11px] uppercase tracking-wider text-violet-700">12-mo TeamMate</p>
                <p className="text-xl md:text-2xl font-extrabold text-violet-700 mt-1">{fmt(tco.vpYear)}</p>
              </div>
              <div className="col-span-2 md:col-span-2 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white p-4 text-center shadow-lg shadow-emerald-500/30">
                <p className="text-[11px] uppercase tracking-widest text-white/90">You save in year 1</p>
                <p className="text-3xl md:text-4xl font-extrabold mt-1">{fmt(Math.max(0, tco.savings))} <span className="text-base md:text-lg font-bold text-white/90">({Math.max(0, tco.pct)}%)</span></p>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 mt-4 text-center">
              Indicative — excludes per-minute call usage. Outbound minute savings on international destinations
              typically add 30–60% on top. Bring 3 months of your Teams call detail report and we'll model your
              tenant exactly.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/contact" className="btn-primary">Get a personalised TCO <ArrowRight size={14} /></Link>
              <Link to="/microsoft-teams" className="btn-ghost">See TeamMate architecture</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* FULL COMPARISON TABLE                                            */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow"><Sparkles size={11} /> Side-by-side</span>
            <h2 className="section-title mt-4">Calling Plans vs TeamMate — feature matrix</h2>
            <p className="section-sub mt-3">
              The same Microsoft Teams client on both sides. The differences are in the carrier behind it.
            </p>
          </div>

          <div className="max-w-5xl mx-auto rounded-2xl border border-slate-200 overflow-hidden shadow-sm bg-white">
            {/* Header */}
            <div className="grid grid-cols-3 text-sm">
              <div className="bg-slate-50 px-4 py-4 font-bold text-slate-700 border-b border-slate-200">Capability</div>
              <div className="bg-slate-50 px-4 py-4 font-bold text-slate-700 border-b border-slate-200 text-center">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Microsoft</div>
                Calling Plans
              </div>
              <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 px-4 py-4 font-bold text-white border-b border-slate-200 text-center">
                <div className="text-[11px] uppercase tracking-wider text-violet-100/90">VoIPinger</div>
                TeamMate
              </div>
            </div>

            {/* Rows grouped */}
            {Object.entries(groups).map(([groupName, rows]) => (
              <React.Fragment key={groupName}>
                <div className="col-span-3 px-4 py-2.5 bg-violet-50 border-b border-violet-100 text-[11px] font-extrabold uppercase tracking-widest text-violet-700">
                  {groupName}
                </div>
                <div className="grid grid-cols-3 text-sm">
                  {rows.map((row, idx) => (
                    <React.Fragment key={`${groupName}-${idx}`}>
                      <div className={`px-4 py-3 border-b border-slate-100 text-slate-900 ${idx % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                        {row.label}
                      </div>
                      <div className={`px-4 py-3 border-b border-slate-100 text-center ${idx % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                        <Cell v={row.mcp} positive={false} />
                      </div>
                      <div className={`px-4 py-3 border-b border-slate-100 text-center bg-violet-50/40`}>
                        <Cell v={row.vp} positive />
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>

          <p className="text-xs text-slate-500 text-center mt-6">
            Microsoft Calling Plans pricing reflects publicly listed UK Domestic / International rates as
            of 2026. Plan and add-on availability varies by Microsoft 365 SKU and licensing agreement.
          </p>
        </div>
      </section>

      {/* ============================================================== */}
      {/* WHY THE MARGIN EXISTS                                            */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Why the saving is real</span>
            <h2 className="section-title mt-4">Where 30–60% goes when you switch</h2>
            <p className="section-sub mt-3">
              The savings aren't a gimmick. They come from four specific places — and they compound.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {[
              {
                i: PoundSterling, t: 'Lower per-user seat fee',
                d: "TeamMate seats are priced as a focused Direct Routing licence — typically £8/user/mo versus £10–£18 for a Microsoft Domestic Calling Plan. Multiply across a 50-user tenant and you're already £3,600–£6,000 ahead before a call is placed."
              },
              {
                i: Globe2, t: 'Wholesale call rates',
                d: 'International minutes via Calling Plans are billed at retail Microsoft rates. TeamMate routes those same destinations at carrier-wholesale rates — typically 50–80% lower per minute. Heavy international callers see the biggest gain.'
              },
              {
                i: ShieldCheck, t: 'No add-on creep',
                d: 'Compliance recording, PCI pause/resume, sentiment analysis, advanced contact-centre — all bundled with TeamMate. The Microsoft equivalents are sold as Teams Premium / Compliance Recording add-ons at extra per-user cost.'
              },
              {
                i: Server, t: 'SBC included',
                d: 'TeamMate ships with a Microsoft-certified Ribbon SBC at no extra cost. The DIY Direct Routing route demands SBC hardware, co-location, certificate management and Powershell expertise — none of that is on you here.'
              }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                  <c.i size={22} />
                </div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Calling Plans vs TeamMate — FAQ" />
      <CtaBanner
        eyebrow="Migrate Teams in 48 hours"
        title="Bring 3 months of Teams call detail. We'll model your tenant."
        subtitle="Free 30-minute review with our engineering team. You leave with a personalised 12-month TCO and a migration plan you can hand to procurement."
      />
    </>
  );
}

/* ----------------------------- helpers ----------------------------- */
function Cell({ v, positive }) {
  if (v === true) {
    return <Check size={16} className={positive ? 'text-violet-600 inline' : 'text-emerald-500 inline'} />;
  }
  if (v === false) {
    return <X size={16} className="text-rose-400 inline" />;
  }
  // string value
  return <span className={positive ? 'font-bold text-violet-700' : 'text-slate-700'}>{v}</span>;
}
